import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

// Helper function to format duration from seconds to human readable
const formatDuration = (seconds: number | string): string => {
  const numSeconds = typeof seconds === 'number' ? seconds : parseFloat(seconds as string);
  if (isNaN(numSeconds) || numSeconds < 0) {
    return '0min';
  }
  
  const totalMinutes = Math.round(numSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}min`;
  } else {
    return `${mins}min`;
  }
};

// Helper function to format distance from meters to km
const formatDistance = (meters: number | string): string => {
  const numMeters = typeof meters === 'number' ? meters : parseFloat(meters as string);
  if (isNaN(numMeters) || numMeters < 0) {
    return '0.0km';
  }
  const kilometers = numMeters / 1000;
  return `${kilometers.toFixed(1)}km`;
};

// Helper function to format elevation gain (already in heightmeters)
const formatElevationGain = (heightmeters: number | string): string => {
  const numHeightmeters = typeof heightmeters === 'number' ? heightmeters : parseFloat(heightmeters as string);
  if (isNaN(numHeightmeters) || numHeightmeters < 0) {
    return '0m';
  }
  return `${Math.round(numHeightmeters)}m`;
};

interface LoadRouteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  routeName: string;
  currentSessionInfo: {
    pointCount: number;
    routeName: string | null;
    hasUnsavedChanges: boolean;
    routeMetrics?: {
      distance: number | string;
      duration: number | string;
      elevationGain: number | string;
    } | null;
  };
}

export const LoadRouteConfirmationModal: React.FC<LoadRouteConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  routeName,
  currentSessionInfo
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="load-route-confirmation-title"
    >
      <DialogTitle 
        id="load-route-confirmation-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'warning.main'
        }}
      >
        <WarningIcon />
        Replace Current Route?
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have an active planning session that will be replaced.
        </Alert>
        
        <DialogContentText component="div">
          <Typography variant="body1" gutterBottom>
            You are about to load the saved route: <strong>"{routeName}"</strong>
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Current planning session:
          </Typography>
          
          <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
            <Typography variant="body2">
              • <strong>{currentSessionInfo.pointCount}</strong> route points
            </Typography>
            {currentSessionInfo.routeName && (
              <Typography variant="body2">
                • Route name: <strong>"{currentSessionInfo.routeName}"</strong>
              </Typography>
            )}
            {currentSessionInfo.routeMetrics && (
              <>
                <Typography variant="body2">
                  • Distance: <strong>{formatDistance(currentSessionInfo.routeMetrics.distance)}</strong>
                </Typography>
                <Typography variant="body2">
                  • Duration: <strong>{formatDuration(currentSessionInfo.routeMetrics.duration)}</strong>
                </Typography>
                <Typography variant="body2">
                  • Elevation gain: <strong>{formatElevationGain(currentSessionInfo.routeMetrics.elevationGain)}</strong>
                </Typography>
              </>
            )}
            {currentSessionInfo.hasUnsavedChanges && (
              <Typography variant="body2">
                • <span style={{ color: '#f57c00' }}>Has unsaved changes</span>
              </Typography>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading the saved route will replace your current planning session. 
            Any unsaved work will be lost.
          </Typography>
        </DialogContentText>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          color="warning"
          autoFocus
        >
          Replace Current Route
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadRouteConfirmationModal;
